import cn from 'classnames';
import { Icon, Stack, Text } from '@pairy/pds-react';
import { MouseEvent, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { isDisableHelpersAtom } from '../../../../store/index.atom';
import {
  v2ZoomAtom,
  v2ZoomFitModeAtom,
} from '../../../../store/v2/viewer.atom';
import useClickOutside from '../../../../hooks/useClickOutside';
import {
  clampV2Zoom,
  getNextZoomStep,
  V2_ZOOM_MAX,
  V2_ZOOM_MIN,
  V2_ZOOM_PRESETS,
} from '../../zoom.constants';

import * as Styled from './index.styles';

interface Props {
  /** fit-width 배율(현재 페이지 기준) */
  fitWidthZoom: number;
  isFullscreenActive: boolean;
  toggleFullscreen: () => void;
  translateKoToEn: (item: string) => string;
}

/**
 * V2 줌 컨트롤. V1 ZoomButton과 동일한 pds Button 알약([＋][비율][－])을
 * 유지하되, 가운데 비율 표시는 드롭다운 트리거로서 기본 숨김 상태에서
 * 호버(또는 메뉴 열림) 시에만 노출된다. 드롭다운에는 배율 프리셋과
 * 가로 맞춤/페이지 맞춤/전체화면을 그룹핑한다.
 */
const ZoomControls = ({
  fitWidthZoom,
  isFullscreenActive,
  toggleFullscreen,
  translateKoToEn,
}: Props) => {
  const [zoom, setZoom] = useAtom(v2ZoomAtom);
  const [fitMode, setFitMode] = useAtom(v2ZoomFitModeAtom);
  const setZoomValue = useSetAtom(v2ZoomAtom);
  const isDisableHelpers = useAtomValue(isDisableHelpersAtom);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  useClickOutside([pillRef], () => setIsMenuOpen(false));

  const applyCustomZoom = (value: number) => {
    setZoom(clampV2Zoom(value));
    setFitMode('custom');
  };

  const applyFitPage = () => {
    setZoomValue(1);
    setFitMode('fitPage');
  };

  const applyFitWidth = () => {
    setZoomValue(clampV2Zoom(fitWidthZoom));
    setFitMode('fitWidth');
  };

  const closeMenu = () => setIsMenuOpen(false);

  const handleClickZoomIn = () => {
    if (zoom >= V2_ZOOM_MAX) {
      return;
    }
    applyCustomZoom(getNextZoomStep(zoom, 'in'));
  };

  const handleClickZoomOut = () => {
    if (zoom <= V2_ZOOM_MIN) {
      return;
    }
    applyCustomZoom(getNextZoomStep(zoom, 'out'));
  };

  return (
    <Styled.PillWrapper
      ref={pillRef}
      className={cn({ 'is-menu-open': isMenuOpen })}
    >
      <Styled.PillButton
        className={isDisableHelpers ? 'disable' : ''}
        variant="zinc_500"
        radius={9999}
        paddingY={16}
        aria-label={translateKoToEn('zoom-in')}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Stack
          direction="row"
          gap={16}
          justifyContent="center"
          alignItems="center"
        >
          <Styled.PillIcon
            icon="ph:magnifying-glass-plus"
            size={19}
            color={zoom >= V2_ZOOM_MAX ? 'zinc_400' : 'black'}
            onClick={handleClickZoomIn}
          />

          <Styled.PercentArea className="zoom-percent-area">
            <Styled.PercentTrigger
              role="button"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen((prev) => !prev);
              }}
            >
              <Text variant="lg-medium" color="black">
                {Math.round(zoom * 100)}%
              </Text>
              <Icon icon="ph:caret-down" size={14} color="zinc_400" />
            </Styled.PercentTrigger>
          </Styled.PercentArea>

          <Styled.PillIcon
            icon="ph:magnifying-glass-minus"
            size={19}
            color={zoom <= V2_ZOOM_MIN ? 'zinc_400' : 'black'}
            onClick={handleClickZoomOut}
          />
        </Stack>
      </Styled.PillButton>

      {isMenuOpen && (
        <Styled.Dropdown direction="column" gap={2}>
          {V2_ZOOM_PRESETS.map((preset) => (
            <Styled.PresetItem
              key={preset}
              type="button"
              isActive={Math.abs(zoom - preset) < 0.01}
              onClick={(e) => {
                e.stopPropagation();
                applyCustomZoom(preset);
                closeMenu();
              }}
            >
              {Math.round(preset * 100)}%
            </Styled.PresetItem>
          ))}

          <Styled.MenuDivider />

          <Styled.ActionItem
            type="button"
            isActive={fitMode === 'fitWidth'}
            onClick={(e) => {
              e.stopPropagation();
              applyFitWidth();
              closeMenu();
            }}
          >
            <Icon
              icon="ph:arrows-out-line-horizontal"
              size={16}
              color={fitMode === 'fitWidth' ? 'skyblue_500' : 'zinc_700'}
            />
            {translateKoToEn('fit-width')}
          </Styled.ActionItem>
          <Styled.ActionItem
            type="button"
            isActive={fitMode === 'fitPage'}
            onClick={(e) => {
              e.stopPropagation();
              applyFitPage();
              closeMenu();
            }}
          >
            <Icon
              icon="ph:frame-corners"
              size={16}
              color={fitMode === 'fitPage' ? 'skyblue_500' : 'zinc_700'}
            />
            {translateKoToEn('fit-page')}
          </Styled.ActionItem>

          <Styled.MenuDivider />

          <Styled.ActionItem
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
              closeMenu();
            }}
          >
            <Icon
              icon={isFullscreenActive ? 'ph:arrows-in' : 'ph:arrows-out'}
              size={16}
              color="zinc_700"
            />
            {isFullscreenActive
              ? translateKoToEn('exit-fullscreen')
              : translateKoToEn('fullscreen')}
          </Styled.ActionItem>
        </Styled.Dropdown>
      )}
    </Styled.PillWrapper>
  );
};

export default ZoomControls;
